import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Clock } from 'lucide-react';
import SEO from '../components/SEO';
import { getSubscription, getSubscriptionPlanList } from '../services/api';
import { normalizePlan } from '../data/plans';
import { useAuth } from '../context/AuthContext';
import './PaymentResultPage.css';

// iyzico ödemeyi tamamlayınca BE'nin callback'i çalışır, BE de kullanıcıyı
// buraya yönlendirir:
//   /odeme-sonuc?status=success|processing|failed&subscriptionId=<guid>
//
// processing: ödeme alındı ama webhook henüz işlenmedi. GetSubscription yalnızca
// Active aboneliği döndürdüğü için "yok" ile "işleniyor" ayrımını yapmıyor;
// bu yüzden belirli süre yoklayıp sonucu ona göre gösteriyoruz.
const POLL_INTERVAL = 6000;
const POLL_TIMEOUT = 60000;

// TransMeet satın alma akışı kısa ömürlü bir çerez bırakır; ödeme sonucu tek
// adrese döndüğü için ayrımı burada yapıp koyu temalı sayfaya devrediyoruz.
function handOffToTransMeet() {
  try {
    if (!/(?:^|;\s*)payment_return=transmeet(?:;|$)/.test(document.cookie)) return false;
    // Çerezi hemen sil — bir sonraki ödemede yanlış sayfaya gitmesin
    document.cookie = 'payment_return=; Max-Age=0; Path=/; SameSite=Lax';
    window.location.replace('/transmeet/odeme-sonuc' + window.location.search);
    return true;
  } catch {
    return false;
  }
}

// Eski bağlantılarla gelinirse (?durum=basarili) yine doğru sonucu göster
function readStatus(params) {
  const raw = (params.get('status') || '').toLowerCase();
  if (raw === 'success' || raw === 'processing' || raw === 'failed') return raw;

  const legacy = (params.get('durum') || '').toLowerCase();
  if (legacy === 'basarili') return 'success';
  if (legacy === 'basarisiz') return 'failed';
  return 'processing';
}

const PaymentResultPage = () => {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [handoff] = useState(() => handOffToTransMeet());

  const initial = readStatus(params);
  const [state, setState] = useState(initial === 'processing' ? 'checking' : initial);
  const [plan, setPlan] = useState(null);
  const [planNames, setPlanNames] = useState({});
  const timerRef = useRef(null);
  const startedRef = useRef(Date.now());

  const message = params.get('mesaj') || params.get('message');

  // Paket adı BE'den İngilizce geliyor; listedeki Türkçe karşılığını kullan
  useEffect(() => {
    getSubscriptionPlanList()
      .then((list) => {
        const map = {};
        (list || []).map(normalizePlan).forEach((p) => { map[p.id] = p.name; });
        setPlanNames(map);
      })
      .catch(() => {});
  }, []);

  const check = useCallback(async () => {
    try {
      const sub = await getSubscription(user?.candidateId);
      const active = sub && String(sub.status).toUpperCase() === 'ACTIVE';
      if (active) {
        setPlan(sub);
        setState('success');
        return true;
      }
    } catch {
      // "subscription-not-found" da buraya düşer — henüz işlenmemiş olabilir
    }
    return false;
  }, [user?.candidateId]);

  useEffect(() => {
    let cancelled = false;

    // success ile gelindiyse de plan adını göstermek için bir kez sorgula
    if (initial === 'success') {
      check();
      return undefined;
    }
    if (initial !== 'processing') return undefined;

    const tick = async () => {
      if (cancelled) return;
      const done = await check();
      if (done || cancelled) return;

      if (Date.now() - startedRef.current >= POLL_TIMEOUT) {
        setState('slow');
        return;
      }
      timerRef.current = setTimeout(tick, POLL_INTERVAL);
    };

    timerRef.current = setTimeout(tick, POLL_INTERVAL);
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [initial, check]);

  const view = {
    success: {
      icon: <CheckCircle2 size={34} />,
      tone: 'ok',
      title: 'Ödemen alındı',
      text: (planNames[plan?.planId] || plan?.planName)
        ? `${planNames[plan?.planId] || plan.planName} paketin aktif edildi. Tüm özellikleri hemen kullanmaya başlayabilirsin.`
        : 'Aboneliğin aktif edildi. Paketinin sunduğu tüm özellikleri hemen kullanmaya başlayabilirsin.',
    },
    checking: {
      icon: <Loader2 size={34} className="payment-result__spin" />,
      tone: 'wait',
      title: 'İşlemin onaylanıyor',
      text: 'Ödemen alındı, aboneliğin hazırlanıyor. Bu ekranı kapatmana gerek yok, birkaç saniye içinde güncellenecek.',
    },
    slow: {
      icon: <Clock size={34} />,
      tone: 'wait',
      title: 'Onay biraz sürüyor',
      text: 'Ödemen alındı ancak abonelik onayı beklenenden uzun sürdü. Bu genelde birkaç dakika içinde tamamlanır; Aboneliğim sayfasından durumu kontrol edebilirsin.',
    },
    failed: {
      icon: <XCircle size={34} />,
      tone: 'fail',
      title: 'Ödeme tamamlanamadı',
      text: message || 'Ödeme sırasında bir sorun oluştu. Kartından tutar çekilmediyse tekrar deneyebilirsin.',
    },
  }[state];

  // Koyu temaya devredildiyse bu sayfa bir an bile görünmesin
  if (handoff) return null;

  return (
    <div className="payment-result-page">
      <SEO title={state === 'failed' ? 'Ödeme Başarısız' : 'Ödeme Sonucu'} path="/odeme-sonuc" noindex />
      <div className="payment-result__container">
        <div className={`payment-result__icon payment-result__icon--${view.tone}`}>
          {view.icon}
        </div>

        <h1 className="payment-result__title">{view.title}</h1>
        <p className="payment-result__text">{view.text}</p>

        <div className="payment-result__actions">
          {state === 'failed' ? (
            <>
              <Link to="/panel/abonelik" className="payment-result__btn payment-result__btn--primary">
                Tekrar Dene
              </Link>
              <Link to="/panel" className="payment-result__btn">Panele Dön</Link>
            </>
          ) : (
            <>
              <Link to="/panel/abonelik" className="payment-result__btn payment-result__btn--primary">
                Aboneliğimi Gör
              </Link>
              <Link to="/panel" className="payment-result__btn">Panele Dön</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentResultPage;
